import { inject } from '@angular/core';
import { CanMatchFn } from '@angular/router';
import { catchError, map, of } from 'rxjs';
import { SavingArenaApiService } from '../api/saving-arena-api.service';

/**
 * The Shop's master switch, applied to its routes.
 *
 * `CanMatch` rather than `CanActivate` on purpose. A failed `CanActivate` cancels the navigation and
 * leaves the visitor where they were — from a crawler's point of view nothing happened, and the URL
 * keeps whatever status the previous page had. A failed `CanMatch` makes the route simply not exist,
 * so matching carries on to the next route and `/shop/...` lands on the 404 registered right after
 * it. That is the whole point: while the Shop is off its URLs must be gone, not blocked.
 *
 * It also stops the Shop's lazy bundle being fetched at all, which `CanActivate` would not.
 *
 * On error the Shop stays visible. An unreachable settings endpoint is the backend being down, which
 * breaks every page equally — and the alternative, treating "don't know" as off, would 404 a live
 * Shop over a single network blip on a visitor's phone.
 */
export const shopEnabledGuard: CanMatchFn = () => {
  const api = inject(SavingArenaApiService);

  return api.getShopSettings().pipe(
    map((response) => response.data?.enabled !== false),
    catchError(() => of(true)),
  );
};
